import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Store, Network, Building2, Users, Truck, BadgeCheck } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Section";
import { DealerMap } from "@/components/DealerMap";
import { useContent } from "@/context/ContentContext";

const DEALER_STAT_ICONS = {
  store: Store,
  building: Building2,
  network: Network,
  users: Users,
  "map-pin": MapPin,
  truck: Truck,
  "badge-check": BadgeCheck,
};

export default function DealerNetwork() {
  const { content } = useContent();
  const MAP_IMAGE = content.company?.mapImage;
  const summary = content.dealer_summary || {};
  const cmsStats = Array.isArray(content.dealer_stats) ? content.dealer_stats : [];
  // Prefer admin-managed dealer_stats; fall back to auto-derived from map pins.
  const HIGHLIGHTS = cmsStats.length
    ? cmsStats.map((s) => ({
        Icon: DEALER_STAT_ICONS[s.icon] || Store,
        value: s.value,
        label: s.label,
      }))
    : [
        { Icon: Store, value: `${summary.total_dealers || 0}+`, label: "Active Dealers" },
        { Icon: Building2, value: `${summary.total_retailers || 0}+`, label: "Retail Outlets" },
        { Icon: Network, value: `${summary.divisions || 0}`, label: "Divisions Covered" },
      ];
  const REGION_LIST = summary.regions || [];
  return (
    <div data-testid="dealer-network-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img src={MAP_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-secondary">
            Nationwide Reach
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-5xl font-bold leading-[0.95] tracking-tight text-white md:text-6xl">
            Our Dealer Network
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-white/75">
            A strong, reliable distribution network spanning every division of Bangladesh — ensuring
            genuine products are always within reach.
          </p>
        </div>
      </section>

      {/* Highlights */}
      <section className="border-b border-border bg-card">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-border sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.Icon;
            return (
              <Reveal key={h.label} delay={i * 0.08} className="flex items-center gap-5 px-8 py-10">
                <Icon className="h-10 w-10 text-primary" />
                <div>
                  <div className="font-heading text-4xl font-bold text-foreground">{h.value}</div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {h.label}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Interactive Map */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow="Explore The Map"
          title="Find Us Across Bangladesh"
          description="An interactive view of our coverage. Click a pin to see location details. Individual dealer details remain confidential."
        />
        <div className="mb-6 flex flex-wrap items-center gap-6" data-testid="map-legend">
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span className="inline-block h-3.5 w-3.5 rounded-full bg-primary" />
            Dealership Locations
          </span>
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span className="inline-block h-3.5 w-3.5 rounded-full" style={{ background: "#DC2626" }} />
            Retail Locations
          </span>
        </div>
        <Reveal>
          <DealerMap />
        </Reveal>
      </section>

      {/* Regional Coverage */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeading
          eyebrow="Regional Coverage"
          title="Present In Every Division"
          description="Our dealer partners keep genuine parts and lubricants flowing across the country. Dealer details are kept confidential — reach out through our support channel for connections."
        />
        <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {REGION_LIST.map((r, i) => (
            <Reveal key={r.name} delay={(i % 4) * 0.06}>
              <div className="group flex h-full flex-col justify-between bg-card p-6 transition-colors hover:bg-primary">
                <MapPin className="h-6 w-6 text-primary transition-colors group-hover:text-white" />
                <div className="mt-8">
                  <h3 className="font-heading text-lg font-bold tracking-tight text-foreground transition-colors group-hover:text-white">
                    {r.name}
                  </h3>
                  <span className="text-sm font-medium text-muted-foreground transition-colors group-hover:text-white/85">
                    {[
                      r.dealers ? `${r.dealers}+ dealers` : null,
                      r.retailers ? `${r.retailers}+ retailers` : null,
                    ].filter(Boolean).join(" · ") || "Coming soon"}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-sm italic text-muted-foreground">
          * Individual dealer information is not publicly disclosed. Please use the support form for
          dealership enquiries.
        </p>
      </section>

      {/* Become a dealer CTA */}
      <section className="border-t border-border diagonal-stripes bg-card">
        <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8">
          <h2 className="mx-auto max-w-2xl font-heading text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Want To Join Our Network?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Become an authorized RTPL dealer and grow your business with genuine, in-demand
            automotive products.
          </p>
          <Link
            to="/support"
            data-testid="become-dealer-btn"
            className="mt-8 inline-flex items-center gap-2 bg-primary px-8 py-3.5 font-heading text-sm font-bold tracking-wide text-white transition-colors hover:bg-primary/90 shadow-md hover:shadow-lg"
          >
            Apply For Dealership <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
